import React from "react";
import PropTypes from "prop-types";
import * as Mui from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import {
  designColors,
  designFontFamily,
  designShell,
  designTheme,
  designType,
} from "services/constants/designTokens";
import { useScreenPermissions } from "services/utils/screenPermissions";
import { Loader } from "components/Loader";

// Same card Team Workspace > Settings shows to a user without view access.
export const AccessRestricted = ({ title }) => (
  <Mui.ThemeProvider theme={designTheme}>
    <Mui.Box
      sx={{
        ...designShell.pageSurface,
        fontFamily: designFontFamily,
        p: { xs: 2, md: 3 },
      }}
    >
      <Mui.Stack spacing={2}>
        <Mui.Typography component="h1" sx={designType.pageTitle}>
          {title}
        </Mui.Typography>
        <Mui.Box
          sx={{
            ...designShell.sectionCard,
            p: { xs: 4, md: 6 },
            textAlign: "center",
          }}
        >
          <LockOutlined
            sx={{ fontSize: 56, color: designColors.tealDark, mb: 1.5 }}
          />
          <Mui.Typography sx={designType.panelTitle}>
            Access Restricted
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            You do not have permission to view {title}. Please contact your
            administrator.
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  </Mui.ThemeProvider>
);

AccessRestricted.propTypes = { title: PropTypes.string.isRequired };

// Wraps a screen so it only mounts (and only loads its data) for a user who
// may view it, and hands it `permissions` ({ canEdit, canDelete }) to decide
// which actions to show. See services/utils/screenPermissions.js.
export const withScreenPermissions = (Screen, screen, title) => {
  const Guarded = (props) => {
    const permissions = useScreenPermissions(screen);
    if (permissions.loading) return <Loader info="Loading..." />;
    if (!permissions.canView) return <AccessRestricted title={title} />;
    return <Screen {...props} permissions={permissions} />;
  };
  Guarded.displayName = `withScreenPermissions(${title})`;
  return Guarded;
};
